import React from 'react';
import { motion } from 'framer-motion';
interface KofschipMnemonicProps {
  verb: string;
  stem: string;
  lastLetter: string;
  useTE: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function KofschipMnemonic({
  verb,
  stem,
  lastLetter,
  useTE,
  label,
  mode = 'support'
}: KofschipMnemonicProps) {
  const kofschipLetters = ['K', 'O', 'F', 'S', 'C', 'H', 'I', 'P'];
  const isInKofschip = kofschipLetters.includes(lastLetter.toUpperCase());
  // For the "not in kofschip" indicator shown below the grid
  const notInKofschipIndicator = !isInKofschip ? <div className="flex items-center justify-center gap-2 mt-2" data-id="element-12829">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg bg-rose-100 text-rose-600 border-2 border-rose-300 shadow-sm" data-id="element-12830">
        {lastLetter.toUpperCase()}
      </div>
      <span className="text-sm font-semibold text-rose-600" data-id="element-12831">
        ← niet in 't kofschip
      </span>
    </div> : null;
  // ── STIMULUS MODE: compact KOFSCHIP grid + result only ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-12832">
        {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-12833">
            {label}
          </p>}
        {/* Compact KOFSCHIP grid with color coding */}
        <div className="flex items-center gap-1.5" data-id="element-12834">
          <span className="text-base font-black text-slate-400 mr-1" data-id="element-12835">'t</span>
          {kofschipLetters.map(letter => {
          const isMatch = letter === lastLetter.toUpperCase();
          return <div key={letter} className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg shadow-sm
                  ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-id="element-12836">
                {letter}
              </div>;
        })}
        </div>
        {/* Show the tested letter in red if NOT in kofschip */}
        {notInKofschipIndicator}
        {/*
              NOTE (antwoord-lek fix): the previous "stem + -te/-de" result badge
              was REMOVED from stimulus mode. As a stimulus it pre-computed the
              full kofschip decision AND revealed the intact loanword stem-spelling
              — i.e. it handed the learner the exact answer the MC/fill-in item is
              meant to test (e.g. seed stem:"race", useTE:true → showed "race + -te"
              → option "racete"). The KOFSCHIP letter grid + "niet in 't kofschip"
              indicator stay: they present the *mnemonic tool* (and, for klank-vs-
              letter traps like 'race', the deliberate misconception) without
              resolving the answer. The full worked result still lives in SUPPORT
              mode below, shown via the rule/support aid, not in the stimulus.
            */}
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-12837">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 md:p-8 border-2 border-blue-200 shadow-lg" data-id="element-12838">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-12839">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-12840">
            <span className="text-2xl" data-id="element-12841">⛵</span>
            <div className="text-left" data-id="element-12842">
              <div className="text-sm font-bold text-blue-900" data-id="element-12843">
                {label || "'t KOFSCHIP Regel"}
              </div>
              <div className="text-xs text-blue-600 font-semibold" data-id="element-12844">
                Verleden Tijd Spelling
              </div>
            </div>
          </div>
        </div>

        {/* Ezelsbruggetje (Tip) */}
        <div className="mb-6 bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-12845">
          <div className="flex items-start gap-3" data-id="element-12846">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-12847">
              <span className="text-amber-600 text-lg" data-id="element-12848">💡</span>
            </div>
            <div data-id="element-12849">
              <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-12850">
                Ezelsbruggetje
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12851">
                Zit de laatste letter van de stam in{' '}
                <strong data-id="element-12852">'t KOFSCHIP</strong>?
                <br data-id="element-12853" />✅ Ja → <strong data-id="element-12854">-te(n)</strong>
                <br data-id="element-12855" />❌ Nee → <strong data-id="element-12856">-de(n)</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Stap-voor-stap voorbeeld (Compact) */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-emerald-100 shadow-sm" data-id="element-12857">
          <div className="flex items-start gap-3" data-id="element-12858">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-12859">
              <span className="text-emerald-600 text-lg" data-id="element-12860">📝</span>
            </div>
            <div className="w-full" data-id="element-12861">
              <h4 className="text-sm font-bold text-emerald-900 mb-4" data-id="element-12862">
                Voorbeeld: {verb}
              </h4>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-between bg-slate-50 p-4 rounded-lg border border-slate-200" data-id="element-12863">
                <div className="text-center" data-id="element-12864">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12865">Stam</div>
                  <div className="font-bold text-lg text-blue-700" data-id="element-12866">{stem}</div>
                </div>
                <div className="text-slate-400" data-id="element-12867">→</div>
                <div className="text-center" data-id="element-12868">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12869">
                    Laatste letter
                  </div>
                  <div className={`font-bold text-xl ${isInKofschip ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-12870">
                    {lastLetter.toUpperCase()}
                  </div>
                </div>
                <div className="text-slate-400" data-id="element-12871">→</div>
                <div className="text-center" data-id="element-12872">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12873">
                    In 't KOFSCHIP?
                  </div>
                  <div className="font-bold text-lg" data-id="element-12874">
                    {isInKofschip ? '✅ Ja' : '❌ Nee'}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center p-3 rounded-lg border-2 font-bold bg-white shadow-sm" data-id="element-12875">
                Dus: <span className="text-emerald-700" data-id="element-12876">{stem}</span> +{' '}
                <span className="text-emerald-700" data-id="element-12877">
                  {useTE ? 'te(n)' : 'de(n)'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Detailed Steps */}
        <details className="group bg-white/60 rounded-xl border border-blue-100 shadow-sm overflow-hidden" data-id="element-12878">
          <summary className="cursor-pointer p-4 font-semibold text-blue-800 hover:bg-blue-50/50 transition-colors flex items-center justify-between" data-id="element-12879">
            <span data-id="element-12880">Meer uitleg (stap voor stap)</span>
            <span className="text-blue-400 group-open:rotate-180 transition-transform" data-id="element-12881">
              ▼
            </span>
          </summary>
          <div className="p-5 border-t border-blue-100 space-y-6" data-id="element-12882">
            {/* Step 1: Verb → Stem */}
            <div className="flex items-start gap-3" data-id="element-12883">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-12884">
                <span className="text-blue-600 font-bold text-xs" data-id="element-12885">1</span>
              </div>
              <div data-id="element-12886">
                <h4 className="text-sm font-bold text-blue-900 mb-1" data-id="element-12887">
                  Vind de stam
                </h4>
                <p className="text-sm text-slate-600" data-id="element-12888">
                  Haal -en van het hele werkwoord af: <strong data-id="element-12889">{verb}</strong> →{' '}
                  <strong data-id="element-12890">{stem}</strong>
                </p>
              </div>
            </div>

            {/* Step 2: Last Letter Check */}
            <div className="flex items-start gap-3" data-id="element-12891">
              <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-12892">
                <span className="text-cyan-600 font-bold text-xs" data-id="element-12893">2</span>
              </div>
              <div data-id="element-12894">
                <h4 className="text-sm font-bold text-cyan-900 mb-1" data-id="element-12895">
                  Kijk naar de laatste letter
                </h4>
                <p className="text-sm text-slate-600" data-id="element-12896">
                  De laatste letter van de stam is de{' '}
                  <strong data-id="element-12897">{lastLetter.toUpperCase()}</strong>.
                </p>
              </div>
            </div>

            {/* Step 3: KOFSCHIP Check */}
            <div className="flex items-start gap-3" data-id="element-12898">
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-12899">
                <span className="text-indigo-600 font-bold text-xs" data-id="element-12900">3</span>
              </div>
              <div className="w-full" data-id="element-12901">
                <h4 className="text-sm font-bold text-indigo-900 mb-3" data-id="element-12902">
                  Zit deze in 't KOFSCHIP?
                </h4>

                <div className="flex justify-start gap-1.5 mb-3 flex-wrap" data-id="element-12903">
                  <div className="text-lg font-black text-slate-400 mr-1" data-id="element-12904">
                    't
                  </div>
                  {kofschipLetters.map(letter => {
                  const isMatch = letter === lastLetter.toUpperCase();
                  return <div key={letter} className={`w-8 h-8 rounded-md flex items-center justify-center font-black text-sm shadow-sm
                          ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-12905">
                        {letter}
                      </div>;
                })}
                </div>

                {!isInKofschip && <div className="text-sm text-rose-600 font-semibold mb-2" data-id="element-12906">
                    De {lastLetter.toUpperCase()} zit er NIET in.
                  </div>}
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>;
}